import useLanguageRegionCode from 'hooks/useLanguageRegionCode';
import Trail from 'types/Trails/Trail';
import { buildSignupUrl } from 'utils/roadblocks';
import useUser from 'hooks/useUser';
import { useIntl, defineMessages } from '@alltrails/shared/react-intl';
import IconButton from '@alltrails/shared/denali/components/IconButton';
import Expand from '@alltrails/shared/icons/Expand';
import useToggle from '@alltrails/shared/hooks/useToggle';
import SignupModal from 'components/SignupModal';
import logTrailDetailsStaticMapClicked from '@alltrails/analytics/events/logTrailDetailsStaticMapClicked';
import handleRoadblock from 'utils/handleRoadblock';
import * as styles from './ViewFullMap.module.scss';
import CarouselDisplayTrigger from '@alltrails/analytics/enums/CarouselDisplayTrigger';

type Props = {
  trail: Trail;
  staticMapUrl: string;
  webpStaticMapUrl: string;
  href: string;
  lat: number;
  lng: number;
};

const messages = defineMessages({ altExpand: { defaultMessage: 'Expand map' }, altStatic: { defaultMessage: '{name} map' } });

// https://www.figma.com/file/gZaqAej8HzoFkyrjlQ4cNs/Integrating-Brand-Changes?node-id=4771%3A93251&t=kOkfouLHLVYNkuUw-4
const ViewFullMap = ({ trail, href, staticMapUrl, webpStaticMapUrl, lat, lng }: Props) => {
  const intl = useIntl();
  const user = useUser();
  const languageRegionCode = useLanguageRegionCode();
  const [isOpen, toggle] = useToggle();

  const newHref = !user ? buildSignupUrl(href, languageRegionCode) : href;

  function handleClick(e: React.MouseEvent<HTMLAnchorElement>) {
    e.preventDefault();
    logTrailDetailsStaticMapClicked({ trail_id: trail.trail_id });

    if (user) {
      handleRoadblock({ afterRoadblock: href, eventName: 'trail_explore', languageRegionCode, returnToUrl: href, user });
    } else {
      toggle();
    }
  }

  const altStatic = intl.formatMessage(messages.altStatic, {
    name: trail.name
  });

  const altExpand = intl.formatMessage(messages.altExpand);
  return (
    <div className={styles.container}>
      <SignupModal trigger={CarouselDisplayTrigger.ViewStaticMap} isOpen={isOpen} onRequestClose={toggle} onSuccess={toggle} redirectUrl={href} />
      <div itemProp="geo" itemScope itemType="http://schema.org/GeoCoordinates">
        <meta itemProp="latitude" content={String(lat)} className="xlate-none" />
        <meta itemProp="longitude" content={String(lng)} className="xlate-none" />
      </div>
      <a className={styles.link} href={newHref} onClick={handleClick}>
        <picture>
          {webpStaticMapUrl && <source srcSet={webpStaticMapUrl} type="image/webp" />}
          <source srcSet={staticMapUrl} type="image/png" />
          <img src={staticMapUrl} alt={altStatic} className={styles.mapImage} />
        </picture>
        <IconButton
          icon={{ Component: Expand, orientation: 'right' }}
          title={altExpand}
          size="md"
          variant="elevated"
          testId="viewFullMap"
          className={styles.viewFullMap}
        />
      </a>
    </div>
  );
};

export default ViewFullMap;
